
import { useState } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { useCustomers, Customer } from '../../store/customerStore';

export default function Customers() {
  const { customers, addCustomer, updateCustomer } = useCustomers();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [customerType, setCustomerType] = useState<'all' | 'supplier' | 'buyer'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [newCustomer, setNewCustomer] = useState<Omit<Customer, 'id'>>({
    name: '',
    type: '供应商',
    contact: '',
    phone: '',
    address: '',
    totalAmount: 0,
    lastDate: new Date().toISOString().slice(0, 10),
    creditCode: '',
    idCard: '',
    remark: '',
  });

  const filteredCustomers = customers
    .filter(c => customerType === 'all' || c.type === (customerType === 'supplier' ? '供应商' : '客户'))
    .filter(c =>
      searchQuery === '' ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.contact.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone.includes(searchQuery)
    );

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer({ ...customer });
    setShowEditModal(true);
  };

  const handleSaveCustomer = () => {
    if (!editingCustomer) return;
    updateCustomer(editingCustomer);
    setShowEditModal(false);
    setEditingCustomer(null);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 2500);
  };

  const handleAddCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomer.name || !newCustomer.contact || !newCustomer.phone) return;
    addCustomer(newCustomer);
    setShowAddModal(false);
    setNewCustomer({
      name: '',
      type: '供应商',
      contact: '',
      phone: '',
      address: '',
      totalAmount: 0,
      lastDate: new Date().toISOString().slice(0, 10),
      creditCode: '',
      idCard: '',
      remark: '',
    });
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 2500);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">客户管理</h1>
            <p className="text-gray-500 mt-2">管理供应商和销售客户信息</p>
          </div>

          {showSaveSuccess && (
            <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] bg-teal-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center space-x-2">
              <i className="ri-check-line text-lg"></i>
              <span className="text-sm font-medium">客户信息保存成功</span>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setCustomerType('all')}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    customerType === 'all' ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  全部 ({customers.length})
                </button>
                <button
                  onClick={() => setCustomerType('supplier')}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    customerType === 'supplier' ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  供应商 ({customers.filter(c => c.type === '供应商').length})
                </button>
                <button
                  onClick={() => setCustomerType('buyer')}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    customerType === 'buyer' ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  客户 ({customers.filter(c => c.type === '客户').length})
                </button>
              </div>

              <div className="flex items-center space-x-3">
                <div className="relative flex-1 md:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="搜索客户名称、联系人或电话..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  新增客户
                </button>
              </div>
            </div>
          </div>

          {filteredCustomers.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-search-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">未找到相关客户</h3>
              <p className="text-sm text-gray-500 mb-6">尝试调整筛选条件或搜索关键词</p>
              <button
                onClick={() => { setSearchQuery(''); setCustomerType('all'); }}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`bg-white rounded-xl shadow-sm border-l-4 ${
                    customer.type === '供应商' ? 'border-l-teal-500' : 'border-l-amber-500'
                  } border-t border-r border-b border-gray-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        customer.type === '供应商' ? 'bg-teal-100' : 'bg-amber-100'
                      }`}>
                        <i className={`${
                          customer.type === '供应商' ? 'ri-user-line text-teal-600' : 'ri-building-line text-amber-600'
                        } text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-800">{customer.name}</h3>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                          customer.type === '供应商' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {customer.type}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <i className="ri-user-3-line w-5 text-gray-400"></i>
                      <span className="ml-2">{customer.contact}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <i className="ri-phone-line w-5 text-gray-400"></i>
                      <span className="ml-2">{customer.phone}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <i className="ri-map-pin-line w-5 text-gray-400"></i>
                      <span className="ml-2">{customer.address}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">累计交易额</span>
                      <span className="text-sm font-semibold text-gray-800">¥{customer.totalAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-gray-500">最近交易</span>
                      <span className="text-sm text-gray-600">{customer.lastDate}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                        <i className="ri-eye-line mr-1"></i>
                        查看
                      </button>
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        编辑
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 新增客户弹窗 */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">新增客户</h2>
                    <p className="text-sm text-gray-500 mt-1">填写客户基本信息</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <form onSubmit={handleAddCustomer} className="p-6">
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs font-bold text-teal-600">1</span>
                      </div>
                      基本信息
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          客户类型 <span className="text-rose-500">*</span>
                        </label>
                        <select
                          value={newCustomer.type}
                          onChange={(e) => setNewCustomer({ ...newCustomer, type: e.target.value as '供应商' | '客户' })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all cursor-pointer"
                        >
                          <option value="供应商">供应商</option>
                          <option value="客户">客户</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          企业/个人名称 <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="请输入名称"
                          value={newCustomer.name}
                          onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs font-bold text-teal-600">2</span>
                      </div>
                      证件信息
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">统一社会信用代码</label>
                        <input
                          type="text"
                          placeholder="企业填写"
                          value={newCustomer.creditCode}
                          onChange={(e) => setNewCustomer({ ...newCustomer, creditCode: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">身份证号</label>
                        <input
                          type="text"
                          placeholder="个人填写"
                          value={newCustomer.idCard}
                          onChange={(e) => setNewCustomer({ ...newCustomer, idCard: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs font-bold text-teal-600">3</span>
                      </div>
                      联系方式
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          联系人 <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="请输入联系人"
                          value={newCustomer.contact}
                          onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          联系电话 <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          required
                          placeholder="请输入联系电话"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">地址</label>
                        <input
                          type="text"
                          placeholder="请输入详细地址"
                          value={newCustomer.address}
                          onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs font-bold text-teal-600">4</span>
                      </div>
                      其他信息
                    </h3>
                    <div className="pl-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                      <textarea
                        rows={3}
                        placeholder="其他补充信息"
                        value={newCustomer.remark}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setNewCustomer({ ...newCustomer, remark: e.target.value });
                          }
                        }}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none transition-all"
                      ></textarea>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-save-line mr-2"></i>
                      保存
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* 编辑客户弹窗 */}
          {showEditModal && editingCustomer && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-5 rounded-t-2xl flex items-center justify-between z-10">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      editingCustomer.type === '供应商' ? 'bg-teal-100' : 'bg-amber-100'
                    }`}>
                      <i className={`${
                        editingCustomer.type === '供应商' ? 'ri-user-line text-teal-600' : 'ri-building-line text-amber-600'
                      } text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">编辑{editingCustomer.type}信息</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{editingCustomer.name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowEditModal(false); setEditingCustomer(null); }}
                    className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <i className="ri-information-line mr-2 text-teal-600"></i>
                      基本信息
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">客户类型 <span className="text-rose-500">*</span></label>
                        <select
                          value={editingCustomer.type}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, type: e.target.value as '供应商' | '客户' })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all cursor-pointer"
                        >
                          <option value="供应商">供应商</option>
                          <option value="客户">客户</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">企业/个人名称 <span className="text-rose-500">*</span></label>
                        <input
                          type="text"
                          value={editingCustomer.name}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="请输入名称"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <i className="ri-id-card-line mr-2 text-teal-600"></i>
                      证件信息
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">统一社会信用代码</label>
                        <input
                          type="text"
                          value={editingCustomer.creditCode || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, creditCode: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="企业填写"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">身份证号</label>
                        <input
                          type="text"
                          value={editingCustomer.idCard || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, idCard: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="个人填写"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <i className="ri-phone-line mr-2 text-teal-600"></i>
                      联系方式
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">联系人 <span className="text-rose-500">*</span></label>
                        <input
                          type="text"
                          value={editingCustomer.contact}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, contact: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="请输入联系人"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">联系电话 <span className="text-rose-500">*</span></label>
                        <input
                          type="tel"
                          value={editingCustomer.phone}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="请输入联系电话"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">地址</label>
                        <input
                          type="text"
                          value={editingCustomer.address}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                          placeholder="请输入详细地址"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <i className="ri-bar-chart-line mr-2 text-teal-600"></i>
                      交易信息
                      <span className="ml-2 text-xs text-gray-400 font-normal">（系统自动统计，不可修改）</span>
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">累计交易额</label>
                        <input
                          type="text"
                          value={`¥${editingCustomer.totalAmount.toLocaleString()}`}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">最近交易日期</label>
                        <input
                          type="text"
                          value={editingCustomer.lastDate}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <i className="ri-file-text-line mr-2 text-teal-600"></i>
                      备注信息
                    </h4>
                    <div>
                      <textarea
                        rows={3}
                        value={editingCustomer.remark || ''}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setEditingCustomer({ ...editingCustomer, remark: e.target.value });
                          }
                        }}
                        maxLength={500}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white transition-all"
                        placeholder="请输入备注信息"
                      ></textarea>
                      <p className="text-xs text-gray-400 mt-1 text-right">{(editingCustomer.remark || '').length}/500</p>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    <i className="ri-time-line mr-1"></i>
                    修改后立即生效
                  </p>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => { setShowEditModal(false); setEditingCustomer(null); }}
                      className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSaveCustomer}
                      className="px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-save-line mr-2"></i>
                      保存修改
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
